// NewsletterManagement.jsx — landing for the Newsletter package.
// Mounted at /newsletter-management. Promotes the (previously
// EmailManagement-embedded) newsletter screens to a first-class
// product surface so the sidebar can link directly and the schedule
// editor can expose the brand/contentSources knobs that drive the
// AI generation in the worker.
//
// Two tabs:
//   Schedules — recurring cadence rows (NewsletterSchedule). Editor is
//     richer than the EmailManagement tab: brand picker (drives which
//     brand's posts/articles the worker pulls), contentSources picker,
//     recencyWindowDays, maxItemsPerSource.
//   Issues   — individual NewsletterIssue rows (one per scheduler tick).
//     Viewer shows the AI-drafted subject + sections + per-section
//     summary + approve/launch CTA when status === 'draft'.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { get, post, putGeneral, remove } from '../../api';
import { ALL_PERMISSIONS } from 'constants/permissionsConstants';
import SystemFilterBar from '../../components/SystemFilterBar';
import { applySystemFilter, EMPTY_SYSTEM_FILTERS } from '../../helpers/systemFilters';
import useBrandList from '../../hooks/useBrandList';

const TABS = [
  { key: 'schedules', label: 'Schedules', endpoint: '/api/email/newsletters/schedules', perm: 'NEWSLETTER_SCHEDULE_VIEW' },
  { key: 'issues',    label: 'Issues',    endpoint: '/api/email/newsletters/issues',    perm: 'NEWSLETTER_ISSUE_VIEW' },
  // Subscription widget channels — collect names + emails chat-widget
  // style, into the list the schedule delivers to.
  { key: 'channels',  label: 'Widget',    endpoint: '/api/email/newsletters/channels',  perm: 'NEWSLETTER_CHANNEL_VIEW' },
  // The people collected across all widgets — username + email, tagged by
  // brand. Brand-scoped to the brands the user can access; manual add gated.
  { key: 'subscribers', label: 'Subscribers', endpoint: '/api/email/newsletters/subscribers', perm: 'NEWSLETTER_CHANNEL_VIEW' },
];

const COLUMNS = {
  schedules: [
    ['name', 'Name'],
    ['brand_id', 'Brand'],
    ['frequency', 'Cadence'],
    ['status', 'Status'],
    ['nextDueAt', 'Next due'],
    ['totalIssuesGenerated', '#'],
    ['totalAiCostUSD', 'AI $'],
  ],
  issues: [
    ['subject', 'Subject'],
    ['issueNumber', '#'],
    ['status', 'Status'],
    ['scheduledFor', 'Scheduled'],
    ['aiCostUSD', 'AI $'],
    ['createdAt', 'Created'],
  ],
  channels: [
    ['name', 'Name'],
    ['brand_id', 'Brand'],
    ['autoGate', 'Auto-gate'],
    ['totalSubscribers', 'Subscribers'],
    ['status', 'Status'],
    ['widgetKey', 'Widget key'],
  ],
  subscribers: [
    ['name', 'Name'],
    ['email', 'Email'],
    ['brand_id', 'Brand'],
    ['source', 'Source'],
    ['sourceUrl', 'Page'],
    ['firstSeenAt', 'Subscribed'],
  ],
};

// Format a cell value — dates, money, booleans, nulls.
function fmt(v, key) {
  if (v == null || v === '') return '—';
  if (key === 'nextDueAt' || key === 'scheduledFor' || key === 'createdAt' || key === 'firstSeenAt') {
    try { return new Date(v).toLocaleString(); } catch { return String(v); }
  }
  if (key === 'totalAiCostUSD' || key === 'aiCostUSD') {
    return Number(v).toLocaleString('en-US', { style: 'currency', currency: 'usd', maximumFractionDigits: 4 });
  }
  if (typeof v === 'boolean') return v ? '✓' : '—';
  return String(v);
}

// ── Schedule editor ────────────────────────────────────────────────────
function ScheduleEditor({ initial, onClose, onSaved }) {
  const isEdit = !!(initial && initial._id);
  const [form, setForm] = useState({
    name: initial?.name || '',
    description: initial?.description || '',
    brand_id: initial?.brand_id || '',
    frequency: initial?.frequency || 'weekly',
    dayOfWeek: initial?.dayOfWeek ?? 1,
    dayOfMonth: initial?.dayOfMonth ?? 1,
    hourLocal: initial?.hourLocal ?? 9,
    timezone: initial?.timezone || 'UTC',
    contentSources: initial?.contentSources || ['posts', 'articles'],
    recencyWindowDays: initial?.recencyWindowDays ?? 7,
    maxItemsPerSource: initial?.maxItemsPerSource ?? 5,
    account_id: initial?.account_id || '',
    list_id: initial?.list_id || '',
    template_id: initial?.template_id || '',
    requireApproval: initial?.requireApproval ?? true,
    status: initial?.status || 'active',
  });
  const [brands, setBrands] = useState([]);
  const [mailboxes, setMailboxes] = useState([]);
  const [lists, setLists] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    // The brand picker is the wire to the worker's content-pull logic —
    // it dictates which brand's recent posts/articles/faqs the
    // generator scans when drafting the issue.
    (async () => {
      try { const r = await get('/brands'); setBrands((r && r.data) || []); } catch (_) {}
      try { const r = await get('/api/email/accounts'); setMailboxes((r && r.data) || []); } catch (_) {}
      try { const r = await get('/api/email/lists'); setLists((r && r.data) || []); } catch (_) {}
      try { const r = await get('/api/email/templates'); setTemplates((r && r.data) || []); } catch (_) {}
    })();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleSource = (s) => set('contentSources', form.contentSources.includes(s) ? form.contentSources.filter((x) => x !== s) : [...form.contentSources, s]);

  const submit = async () => {
    if (!form.name) return setErr('Name is required');
    if (!form.account_id) return setErr('Pick the mailbox to send from');
    if (!form.list_id) return setErr('Pick the audience list');
    setBusy(true); setErr(null);
    try {
      if (isEdit) {
        await putGeneral(`/api/email/newsletters/schedules/${initial._id}`, form);
      } else {
        await post('/api/email/newsletters/schedules', form, false);
      }
      onSaved && onSaved();
    } catch (e) {
      setErr((e && (e.message || e?.response?.data?.error)) || 'Save failed');
    } finally { setBusy(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: '#fff', width: 720, maxWidth: '95vw', maxHeight: '90vh', overflow: 'auto', borderRadius: 8, padding: 24 }} onClick={(e) => e.stopPropagation()}>
        <div className="d-flex align-items-baseline justify-content-between mb-3">
          <h5 className="mb-0">{isEdit ? 'Edit schedule' : 'New newsletter schedule'}</h5>
          <button className="acc-btn" onClick={onClose}>Close</button>
        </div>
        {err && <div className="alert alert-warning" style={{ fontSize: 13 }}>{err}</div>}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <label style={{ fontSize: 12 }}>Name
            <input className="form-control" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Weekly Roundup" />
          </label>
          <label style={{ fontSize: 12 }}>Brand (drives AI content pull)
            <select className="form-control" value={form.brand_id || ''} onChange={(e) => set('brand_id', e.target.value || null)}>
              <option value="">— all brands / company-wide —</option>
              {brands.map((b) => (<option key={b._id} value={b._id}>{b.name}</option>))}
            </select>
          </label>
        </div>

        <label style={{ fontSize: 12, display: 'block', marginTop: 10 }}>Description
          <input className="form-control" value={form.description} onChange={(e) => set('description', e.target.value)} />
        </label>

        <h6 style={{ marginTop: 16, marginBottom: 8 }}>Cadence</h6>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
          <label style={{ fontSize: 12 }}>Frequency
            <select className="form-control" value={form.frequency} onChange={(e) => set('frequency', e.target.value)}>
              {['daily', 'weekly', 'biweekly', 'monthly'].map((v) => (<option key={v} value={v}>{v}</option>))}
            </select>
          </label>
          <label style={{ fontSize: 12 }}>Day of week
            <select className="form-control" value={form.dayOfWeek} onChange={(e) => set('dayOfWeek', Number(e.target.value))} disabled={form.frequency === 'monthly' || form.frequency === 'daily'}>
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d, i) => (<option key={i} value={i}>{d}</option>))}
            </select>
          </label>
          <label style={{ fontSize: 12 }}>Day of month
            <input type="number" min="1" max="28" className="form-control" value={form.dayOfMonth} onChange={(e) => set('dayOfMonth', Number(e.target.value))} disabled={form.frequency !== 'monthly'} />
          </label>
          <label style={{ fontSize: 12 }}>Hour (local)
            <input type="number" min="0" max="23" className="form-control" value={form.hourLocal} onChange={(e) => set('hourLocal', Number(e.target.value))} />
          </label>
        </div>
        <label style={{ fontSize: 12, marginTop: 10, display: 'block' }}>Timezone
          <input className="form-control" value={form.timezone} onChange={(e) => set('timezone', e.target.value)} placeholder="UTC" />
        </label>

        <h6 style={{ marginTop: 16, marginBottom: 8 }}>AI content sources</h6>
        <div style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>
          Pick what the worker should pull when drafting each issue. The brand selected above
          scopes the search; rows are limited to <strong>recencyWindowDays</strong>, up to
          <strong> maxItemsPerSource</strong> rows per source.
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
          {[
            { v: 'posts', l: 'Recent social posts' },
            { v: 'articles', l: 'SEO articles' },
            { v: 'faqs', l: 'FAQ entries' },
            { v: 'custom-prompt', l: 'Free-form prompt' },
          ].map((s) => (
            <label key={s.v} style={{ fontSize: 12 }}>
              <input type="checkbox" checked={form.contentSources.includes(s.v)} onChange={() => toggleSource(s.v)} /> {s.l}
            </label>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <label style={{ fontSize: 12 }}>Recency window (days)
            <input type="number" min="1" max="365" className="form-control" value={form.recencyWindowDays} onChange={(e) => set('recencyWindowDays', Number(e.target.value))} />
          </label>
          <label style={{ fontSize: 12 }}>Max items per source
            <input type="number" min="1" max="50" className="form-control" value={form.maxItemsPerSource} onChange={(e) => set('maxItemsPerSource', Number(e.target.value))} />
          </label>
        </div>

        <h6 style={{ marginTop: 16, marginBottom: 8 }}>Delivery</h6>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <label style={{ fontSize: 12 }}>Send from mailbox
            <select className="form-control" value={form.account_id} onChange={(e) => set('account_id', e.target.value)}>
              <option value="">— pick mailbox —</option>
              {mailboxes.map((m) => (<option key={m._id} value={m._id}>{m.name} &lt;{m.fromAddress}&gt;</option>))}
            </select>
          </label>
          <label style={{ fontSize: 12 }}>Audience list
            <select className="form-control" value={form.list_id} onChange={(e) => set('list_id', e.target.value)}>
              <option value="">— pick list —</option>
              {lists.map((l) => (<option key={l._id} value={l._id}>{l.name} ({l.memberCount || 0} contacts)</option>))}
            </select>
          </label>
        </div>
        <label style={{ fontSize: 12, marginTop: 10, display: 'block' }}>Template (optional)
          <select className="form-control" value={form.template_id || ''} onChange={(e) => set('template_id', e.target.value || null)}>
            <option value="">— generate from scratch each issue —</option>
            {templates.map((t) => (<option key={t._id} value={t._id}>{t.name} ({t.kind})</option>))}
          </select>
        </label>

        <div style={{ display: 'flex', gap: 18, marginTop: 14, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ fontSize: 12 }}>
            <input type="checkbox" checked={form.requireApproval} onChange={(e) => set('requireApproval', e.target.checked)} /> Require human approval before sending
          </label>
          <label style={{ fontSize: 12 }}>Status
            <select className="form-control form-control-sm d-inline-block ml-2" style={{ width: 120 }} value={form.status} onChange={(e) => set('status', e.target.value)}>
              <option value="active">active</option>
              <option value="paused">paused</option>
            </select>
          </label>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <button className="acc-btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="acc-btn acc-btn-primary" onClick={submit} disabled={busy}>
            {busy ? 'Saving…' : (isEdit ? 'Save' : 'Create')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Issue viewer ───────────────────────────────────────────────────────
function IssueViewer({ issue, onClose, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const isSuperAdmin = useSelector((s) => s.user.userInfo?.superAdmin === true);

  const launch = async () => {
    if (!window.confirm(`Approve + launch issue "${issue.subject}"?`)) return;
    setBusy(true); setErr(null);
    try {
      await post(`/api/email/newsletters/issues/${issue._id}/launch`, {}, false);
      onChanged && onChanged();
    } catch (e) { setErr((e && e.message) || 'Launch failed'); }
    finally { setBusy(false); }
  };

  const sections = issue.sections || [];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: '#fff', width: 720, maxWidth: '95vw', maxHeight: '90vh', overflow: 'auto', borderRadius: 8, padding: 24 }} onClick={(e) => e.stopPropagation()}>
        <div className="d-flex align-items-baseline justify-content-between mb-2">
          <h5 className="mb-0">Issue #{issue.issueNumber} · {issue.status}</h5>
          <button className="acc-btn" onClick={onClose}>Close</button>
        </div>
        {err && <div className="alert alert-warning" style={{ fontSize: 13 }}>{err}</div>}
        <div style={{ fontSize: 13, color: '#525252', marginBottom: 8 }}>
          <strong>Subject:</strong> {issue.subject || <em>(empty)</em>}
        </div>
        {issue.preheader && (
          <div style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>Preheader: {issue.preheader}</div>
        )}
        {issue.introMarkdown && (
          <div style={{ background: '#fafbfc', padding: 12, borderRadius: 6, fontSize: 13, whiteSpace: 'pre-wrap', marginBottom: 12 }}>
            {issue.introMarkdown}
          </div>
        )}
        {sections.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <h6 style={{ fontSize: 13, marginBottom: 6 }}>Sections ({sections.length})</h6>
            {sections.map((s, i) => (
              <div key={i} style={{ border: '1px solid #eee', borderRadius: 6, padding: 10, marginBottom: 8 }}>
                <div style={{ fontSize: 11, color: '#888' }}>{s.sourceType}{s.sourceId ? ` · ${s.sourceId}` : ''}</div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{s.title || <em>(no title)</em>}</div>
                <div style={{ fontSize: 12, marginTop: 4, color: '#525252', whiteSpace: 'pre-wrap' }}>{s.summary}</div>
                {s.ctaUrl && (
                  <div style={{ fontSize: 11, marginTop: 4 }}>
                    <a href={s.ctaUrl} target="_blank" rel="noopener noreferrer">{s.ctaLabel || s.ctaUrl}</a>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {issue.outroMarkdown && (
          <div style={{ background: '#fafbfc', padding: 12, borderRadius: 6, fontSize: 13, whiteSpace: 'pre-wrap', marginBottom: 12 }}>
            {issue.outroMarkdown}
          </div>
        )}
        <div style={{ fontSize: 11, color: '#888', marginBottom: 12 }}>
          {isSuperAdmin && (
            <>AI cost: ${Number(issue.aiCostUSD || 0).toFixed(4)} · {issue.llmCallCount || 0} call{(issue.llmCallCount || 0) === 1 ? '' : 's'}</>
          )}
          {issue.lastError && <span style={{ color: '#a71d2a' }}>{isSuperAdmin ? ' · ' : ''}error: {issue.lastError}</span>}
        </div>
        {issue.status === 'draft' && (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button className="acc-btn acc-btn-primary" onClick={launch} disabled={busy}>
              {busy ? 'Launching…' : 'Approve + Launch'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Install snippet ────────────────────────────────────────────────────
// Shown in the channel editor once a widgetKey exists. Mirrors the chat
// widget's InstallSnippets but points at /newsletter-widget.js.
function InstallSnippet({ widgetKey }) {
  const [copied, setCopied] = useState(false);
  const origin = window.location.origin;
  const snippet = `<!-- Prigmar newsletter widget -->
<script src="${origin}/newsletter-widget.js"
        data-widget-key="${widgetKey}"
        async></script>`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) { /* clipboard blocked — user can still select + copy */ }
  };
  return (
    <div style={{ background: '#fafbfc', borderRadius: 6, padding: 10, marginTop: 12, fontSize: 11 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <div style={{ fontWeight: 600 }}>Install on your site — paste before &lt;/body&gt;</div>
        <button type="button" className="acc-btn" style={{ fontSize: 10, padding: '2px 8px' }} onClick={copy}>
          {copied ? 'Copied ✓' : 'Copy'}
        </button>
      </div>
      <pre style={{ margin: 0, fontSize: 11, color: '#525252', background: '#fff', border: '1px solid #e5e5e5', padding: 10, borderRadius: 4, overflow: 'auto', whiteSpace: 'pre' }}>
{snippet}
      </pre>
    </div>
  );
}

// ── Confirm-secret field ───────────────────────────────────────────────
// Shows the per-channel server-to-server double-opt-in confirm secret next to
// the embed snippet. The secret is select:false on the model, so it's fetched
// on demand (Reveal) rather than shipped with the channel list. Copy + Rotate.
// Rotation invalidates any outstanding integration until the new value is
// pasted upstream — so it's gated behind a confirm().
function ConfirmSecretField({ channelId }) {
  const [secret, setSecret] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState('');

  const reveal = async () => {
    setBusy(true); setErr('');
    try {
      const r = await get(`/api/email/newsletters/channels/${channelId}/confirm-secret`);
      setSecret((r && r.data && r.data.confirmSecret) || '');
    } catch (e) { setErr((e && (e.message || e?.response?.data?.error)) || 'Could not load secret'); }
    finally { setBusy(false); }
  };

  const rotate = async () => {
    if (!window.confirm('Rotate the confirm secret? Any upstream integration using the old value stops working until you paste the new one.')) return;
    setBusy(true); setErr('');
    try {
      const r = await post(`/api/email/newsletters/channels/${channelId}/rotate-confirm-secret`, {}, false);
      setSecret((r && r.data && r.data.confirmSecret) || '');
    } catch (e) { setErr((e && (e.message || e?.response?.data?.error)) || 'Rotate failed'); }
    finally { setBusy(false); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(secret); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (_) {}
  };

  return (
    <div style={{ background: '#fafbfc', borderRadius: 6, padding: 10, marginTop: 12, fontSize: 11 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>Server-to-server confirm secret</div>
      <div style={{ color: '#888', marginBottom: 6, lineHeight: 1.5 }}>
        Send in the <code>X-Confirm-Secret</code> header when calling <code>POST /public-newsletter/&lt;key&gt;/confirm</code> to mark a subscriber double-opt-in verified. Keep it secret. Rotation invalidates outstanding integrations.
      </div>
      {err && <div style={{ color: '#a71d2a', marginBottom: 6 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input
          className="form-control"
          style={{ fontSize: 11, fontFamily: 'monospace' }}
          readOnly
          value={secret || '••••••••••••••••••••••••••••••••'}
          onFocus={(e) => e.target.select()}
        />
        {secret
          ? <button type="button" className="acc-btn" style={{ fontSize: 10, padding: '2px 8px', whiteSpace: 'nowrap' }} onClick={copy}>{copied ? 'Copied ✓' : 'Copy'}</button>
          : <button type="button" className="acc-btn" style={{ fontSize: 10, padding: '2px 8px', whiteSpace: 'nowrap' }} onClick={reveal} disabled={busy}>{busy ? '…' : 'Reveal'}</button>}
        <button type="button" className="acc-btn" style={{ fontSize: 10, padding: '2px 8px', whiteSpace: 'nowrap' }} onClick={rotate} disabled={busy}>Rotate</button>
      </div>
    </div>
  );
}

// ── Embed modal ────────────────────────────────────────────────────────
// A one-click way to grab a widget's install snippet WITHOUT entering the
// full editor — so a brand can copy + paste the embed in seconds. Also
// nudges the two things that make multi-placement work: spin up more
// widgets for different layouts, and label each placement via
// data-widget-source so the Subscribers view shows where each lead came in.
function EmbedModal({ channel, onClose }) {
  const STYLE_LABEL = { bubble: 'floating bubble', header: 'header banner', middle: 'middle-of-page block', side: 'side panel' };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: '#fff', width: 640, maxWidth: '95vw', maxHeight: '90vh', overflow: 'auto', borderRadius: 8, padding: 24 }} onClick={(e) => e.stopPropagation()}>
        <div className="d-flex align-items-baseline justify-content-between mb-2">
          <h5 className="mb-0">Embed “{channel.name}”</h5>
          <button className="acc-btn" onClick={onClose}>Close</button>
        </div>
        <div style={{ fontSize: 13, color: '#525252' }}>
          Renders as the <strong>{STYLE_LABEL[channel.widgetStyle] || channel.widgetStyle}</strong> layout you configured. Paste it on any page of your site.
        </div>
        {channel.widgetKey
          ? <InstallSnippet widgetKey={channel.widgetKey} />
          : <div style={{ fontSize: 12, color: '#a71d2a', marginTop: 12 }}>This widget has no key yet — open Edit &amp; embed and save once to generate it.</div>}
        <div style={{ fontSize: 11, color: '#888', marginTop: 12, lineHeight: 1.5 }}>
          <strong>Multiple places?</strong> Add <code>data-widget-source="kit-gate"</code> (any label) to the tag to tag where a lead subscribed — it shows in the Subscribers list. For a different layout on another page, create a second widget with that style.
        </div>
      </div>
    </div>
  );
}

// ── Subscribers modal ──────────────────────────────────────────────────
// The names + emails this widget collected, with the provenance meta the
// public subscribe endpoint stamped: source label, page URL, referrer, ip,
// when first/last seen, how many times. Exportable to CSV.
function SubscribersModal({ channel, onClose }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  // Double-opt-in funnel filter: all | verified | pending.
  const [verFilter, setVerFilter] = useState('all');
  useEffect(() => {
    (async () => {
      try {
        const r = await get(`/api/email/newsletters/channels/${channel._id}/subscribers`);
        setRows((r && r.data) || []);
      } catch (e) { setErr((e && (e.message || e?.response?.data?.error)) || 'Load failed'); setRows([]); }
    })();
  }, [channel._id]);

  const date = (v) => { try { return v ? new Date(v).toLocaleString() : '—'; } catch { return '—'; } };

  // Verified funnel counts + the filtered view the table renders.
  const verifiedCount = (rows || []).filter((s) => s.verifiedAt).length;
  const pendingCount = (rows || []).length - verifiedCount;
  const shownRows = (rows || []).filter((s) => (
    verFilter === 'verified' ? !!s.verifiedAt
      : verFilter === 'pending' ? !s.verifiedAt
        : true
  ));

  const exportCsv = () => {
    const head = ['Name', 'Email', 'Verified', 'Verified at', 'Source', 'Page', 'Referrer', 'IP', 'Subscribed', 'Last seen', 'Times', 'Unsubscribed'];
    const esc = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
    const lines = [head.join(',')].concat((rows || []).map((s) => [
      s.name, s.email, s.verifiedAt ? 'yes' : 'no',
      s.verifiedAt ? new Date(s.verifiedAt).toISOString() : '',
      s.source, s.sourceUrl, s.referrer, s.ip,
      s.firstSeenAt ? new Date(s.firstSeenAt).toISOString() : '',
      s.lastSeenAt ? new Date(s.lastSeenAt).toISOString() : '',
      s.subscribeCount, s.unsubscribed ? 'yes' : 'no',
    ].map(esc).join(',')));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${(channel.name || 'newsletter').replace(/[^a-z0-9]+/gi, '-')}-subscribers.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: '#fff', width: 920, maxWidth: '97vw', maxHeight: '90vh', overflow: 'auto', borderRadius: 8, padding: 24 }} onClick={(e) => e.stopPropagation()}>
        <div className="d-flex align-items-baseline justify-content-between mb-2">
          <h5 className="mb-0">Subscribers — {channel.name}</h5>
          <button className="acc-btn" onClick={onClose}>Close</button>
        </div>
        <div className="d-flex align-items-center justify-content-between mb-2" style={{ fontSize: 12, color: '#888' }}>
          <span>
            {rows == null ? 'Loading…' : `${rows.length} collected`}
            {rows && rows.length > 0 && (
              <span style={{ marginLeft: 8 }}>· <strong style={{ color: '#198754' }}>{verifiedCount} verified</strong> · {pendingCount} pending</span>
            )}
          </span>
          {rows && rows.length > 0 && (
            <button className="acc-btn" style={{ fontSize: 11, padding: '2px 10px' }} onClick={exportCsv}>Export CSV</button>
          )}
        </div>
        {rows && rows.length > 0 && (
          <div className="mb-2" style={{ display: 'flex', gap: 6 }}>
            {[['all', `All (${rows.length})`], ['verified', `Verified (${verifiedCount})`], ['pending', `Pending (${pendingCount})`]].map(([key, label]) => (
              <button
                key={key}
                type="button"
                className="acc-btn"
                style={{
                  fontSize: 11, padding: '2px 10px',
                  background: verFilter === key ? '#0F62FE' : '#fff',
                  color: verFilter === key ? '#fff' : '#525252',
                  borderRadius: 12,
                }}
                onClick={() => setVerFilter(key)}
              >{label}</button>
            ))}
          </div>
        )}
        {err && <div className="alert alert-warning" style={{ fontSize: 13 }}>{err}</div>}
        {rows && rows.length === 0 && !err && (
          <div className="em-stub" style={{ padding: 16, background: '#fafbfc', border: '1px dashed #ccc', borderRadius: 6, color: '#888', fontSize: 13 }}>
            No subscribers yet from this widget. Once it's embedded and someone opts in, every name + email + where they came from shows here.
          </div>
        )}
        {rows && rows.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table className="table table-sm" style={{ fontSize: 12 }}>
              <thead>
                <tr>
                  <th>Name</th><th>Email</th><th title="Double-opt-in confirmed via the email link">Verified</th><th>Source</th><th>Page</th><th>Subscribed</th><th title="How many times this email subscribed">×</th><th />
                </tr>
              </thead>
              <tbody>
                {shownRows.map((s) => (
                  <tr key={s._id} style={s.unsubscribed ? { opacity: 0.5 } : null}>
                    <td>{s.name || '—'}</td>
                    <td>{s.email}</td>
                    <td>{s.verifiedAt
                      ? <span style={{ color: '#198754' }} title={`Verified ${date(s.verifiedAt)}${s.verifiedSource ? ` · ${s.verifiedSource}` : ''}`}>✓</span>
                      : <span style={{ color: '#bbb' }} title="Not yet confirmed">—</span>}</td>
                    <td><span className="badge badge-light" style={{ fontSize: 11 }}>{s.source || '—'}</span></td>
                    <td style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.sourceUrl ? <a href={s.sourceUrl} target="_blank" rel="noreferrer" title={s.sourceUrl}>{s.sourceUrl}</a> : '—'}
                    </td>
                    <td title={s.ip ? `IP ${s.ip}` : ''}>{date(s.firstSeenAt)}</td>
                    <td>{s.subscribeCount || 1}</td>
                    <td>{s.unsubscribed ? <span style={{ color: '#a71d2a', fontSize: 11 }}>unsub</span> : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Channel editor ─────────────────────────────────────────────────────
// A subscription widget channel. Collects names + emails into the chosen
// list — exactly the audience a schedule above delivers to.
function ChannelEditor({ initial, onClose, onSaved }) {
  const isEdit = !!(initial && initial._id);
  const [form, setForm] = useState({
    name: initial?.name || '',
    brand_id: initial?.brand_id || '',
    widgetColor: initial?.widgetColor || '#0F62FE',
    widgetHeadline: initial?.widgetHeadline || 'Subscribe to our newsletter',
    widgetSubtext: initial?.widgetSubtext || 'Get our best updates in your inbox.',
    widgetButtonLabel: initial?.widgetButtonLabel || 'Subscribe',
    widgetSuccessMessage: initial?.widgetSuccessMessage || 'Thanks! You are subscribed.',
    widgetStyle: initial?.widgetStyle || 'bubble',
    collectName: initial?.collectName ?? true,
    autoGate: initial?.autoGate ?? false,
    autoGateDelaySeconds: initial?.autoGateDelaySeconds ?? 8,
    list_id: initial?.list_id || '',
    status: initial?.status || 'connected',
  });
  const [brands, setBrands] = useState([]);
  const [lists, setLists] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [widgetKey, setWidgetKey] = useState(initial?.widgetKey || '');

  useEffect(() => {
    (async () => {
      try { const r = await get('/brands'); setBrands((r && r.data) || []); } catch (_) {}
      try { const r = await get('/api/email/lists'); setLists((r && r.data) || []); } catch (_) {}
    })();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name) return setErr('Name is required');
    setBusy(true); setErr(null);
    try {
      if (isEdit) {
        await putGeneral(`/api/email/newsletters/channels/${initial._id}`, form);
      } else {
        const r = await post('/api/email/newsletters/channels', form, false);
        if (r && r.data && r.data.widgetKey) setWidgetKey(r.data.widgetKey);
      }
      onSaved && onSaved();
    } catch (e) {
      setErr((e && (e.message || e?.response?.data?.error)) || 'Save failed');
    } finally { setBusy(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: '#fff', width: 640, maxWidth: '95vw', maxHeight: '90vh', overflow: 'auto', borderRadius: 8, padding: 24 }} onClick={(e) => e.stopPropagation()}>
        <div className="d-flex align-items-baseline justify-content-between mb-3">
          <h5 className="mb-0">{isEdit ? 'Edit subscription widget' : 'New subscription widget'}</h5>
          <button className="acc-btn" onClick={onClose}>Close</button>
        </div>
        {err && <div className="alert alert-warning" style={{ fontSize: 13 }}>{err}</div>}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <label style={{ fontSize: 12 }}>Name
            <input className="form-control" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Homepage signup" />
          </label>
          <label style={{ fontSize: 12 }}>Brand
            <select className="form-control" value={form.brand_id || ''} onChange={(e) => set('brand_id', e.target.value || null)}>
              <option value="">— company-wide —</option>
              {brands.map((b) => (<option key={b._id} value={b._id}>{b.name}</option>))}
            </select>
          </label>
        </div>

        <h6 style={{ marginTop: 16, marginBottom: 8 }}>Widget appearance</h6>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <label style={{ fontSize: 12 }}>Style
            <select className="form-control" value={form.widgetStyle} onChange={(e) => set('widgetStyle', e.target.value)}>
              <option value="bubble">Floating bubble</option>
              <option value="header">Header banner</option>
              <option value="middle">Middle-of-page text</option>
              <option value="side">Side menu</option>
            </select>
          </label>
          <label style={{ fontSize: 12 }}>Accent color
            <input type="color" className="form-control" style={{ height: 38 }} value={form.widgetColor} onChange={(e) => set('widgetColor', e.target.value)} />
          </label>
          <label style={{ fontSize: 12 }}>Audience list (where subscribers land)
            <select className="form-control" value={form.list_id || ''} onChange={(e) => set('list_id', e.target.value || null)}>
              <option value="">— pick list —</option>
              {lists.map((l) => (<option key={l._id} value={l._id}>{l.name} ({l.memberCount || 0} contacts)</option>))}
            </select>
          </label>
        </div>
        <label style={{ fontSize: 12, display: 'block', marginTop: 10 }}>Headline
          <input className="form-control" value={form.widgetHeadline} onChange={(e) => set('widgetHeadline', e.target.value)} />
        </label>
        <label style={{ fontSize: 12, display: 'block', marginTop: 10 }}>Subtext
          <input className="form-control" value={form.widgetSubtext} onChange={(e) => set('widgetSubtext', e.target.value)} />
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 10 }}>
          <label style={{ fontSize: 12 }}>Button label
            <input className="form-control" value={form.widgetButtonLabel} onChange={(e) => set('widgetButtonLabel', e.target.value)} />
          </label>
          <label style={{ fontSize: 12 }}>Success message
            <input className="form-control" value={form.widgetSuccessMessage} onChange={(e) => set('widgetSuccessMessage', e.target.value)} />
          </label>
        </div>

        <h6 style={{ marginTop: 16, marginBottom: 8 }}>Behaviour</h6>
        <div style={{ display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ fontSize: 12 }}>
            <input type="checkbox" checked={form.collectName} onChange={(e) => set('collectName', e.target.checked)} /> Ask for a name
          </label>
          <label style={{ fontSize: 12 }}>
            <input type="checkbox" checked={form.autoGate} onChange={(e) => set('autoGate', e.target.checked)} /> Auto newsletter gate (proactively open)
          </label>
          <label style={{ fontSize: 12 }}>Gate delay (s)
            <input type="number" min="0" max="600" className="form-control form-control-sm d-inline-block ml-2" style={{ width: 90 }}
              value={form.autoGateDelaySeconds} onChange={(e) => set('autoGateDelaySeconds', Number(e.target.value))} disabled={!form.autoGate} />
          </label>
          <label style={{ fontSize: 12 }}>Status
            <select className="form-control form-control-sm d-inline-block ml-2" style={{ width: 130 }} value={form.status} onChange={(e) => set('status', e.target.value)}>
              <option value="connected">connected</option>
              <option value="inactive">inactive</option>
            </select>
          </label>
        </div>

        {widgetKey ? <InstallSnippet widgetKey={widgetKey} /> : (
          <div style={{ fontSize: 11, color: '#888', marginTop: 12 }}>Save to generate the embed snippet.</div>
        )}

        {isEdit && initial?._id && <ConfirmSecretField channelId={initial._id} />}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <button className="acc-btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="acc-btn acc-btn-primary" onClick={submit} disabled={busy}>
            {busy ? 'Saving…' : (isEdit ? 'Save' : 'Create')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Add-target modal ───────────────────────────────────────────────────
// Manually add a subscriber (name + email) to a brand the caller can access.
// The brand picker is limited to `brands` (the caller's accessible brands),
// and the backend re-checks access — so a user can only add targets to their
// own brands. Optionally attach to a widget so the target joins that list.
function AddTargetModal({ brands, onClose, onSaved }) {
  const [form, setForm] = useState({ name: '', email: '', brand_id: (brands && brands.length === 1 ? brands[0]._id : ''), channelId: '' });
  const [channels, setChannels] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => {
    (async () => { try { const r = await get('/api/email/newsletters/channels'); setChannels((r && r.data) || []); } catch (_) {} })();
  }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const brandChannels = channels.filter((c) => form.brand_id && String(c.brand_id || '') === String(form.brand_id));

  const submit = async () => {
    if (!form.email) return setErr('Email is required');
    if (!form.brand_id) return setErr('Pick a brand');
    setBusy(true); setErr(null);
    try {
      await post('/api/email/newsletters/subscribers', form, false);
      onSaved && onSaved();
    } catch (e) {
      setErr((e && (e.message || e?.response?.data?.error)) || 'Save failed');
    } finally { setBusy(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: '#fff', width: 480, maxWidth: '95vw', borderRadius: 8, padding: 24 }} onClick={(e) => e.stopPropagation()}>
        <div className="d-flex align-items-baseline justify-content-between mb-3">
          <h5 className="mb-0">Add a newsletter target</h5>
          <button className="acc-btn" onClick={onClose}>Close</button>
        </div>
        {err && <div className="alert alert-warning" style={{ fontSize: 13 }}>{err}</div>}
        {(!brands || brands.length === 0) ? (
          <div style={{ fontSize: 13, color: '#a71d2a' }}>You don't have access to any brand. Ask an admin for brand access to add targets.</div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <label style={{ fontSize: 12 }}>Name
                <input className="form-control" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Jane Doe" />
              </label>
              <label style={{ fontSize: 12 }}>Email *
                <input className="form-control" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="jane@example.com" />
              </label>
            </div>
            <label style={{ fontSize: 12, display: 'block', marginTop: 10 }}>Brand * (only brands you can access)
              <select className="form-control" value={form.brand_id || ''} onChange={(e) => { set('brand_id', e.target.value); set('channelId', ''); }}>
                <option value="">— pick a brand —</option>
                {brands.map((b) => (<option key={b._id} value={b._id}>{b.name}</option>))}
              </select>
            </label>
            <label style={{ fontSize: 12, display: 'block', marginTop: 10 }}>Widget (optional — adds them to its list)
              <select className="form-control" value={form.channelId || ''} onChange={(e) => set('channelId', e.target.value)} disabled={!form.brand_id}>
                <option value="">— none —</option>
                {brandChannels.map((c) => (<option key={c._id} value={c._id}>{c.name}</option>))}
              </select>
            </label>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
              <button className="acc-btn" onClick={onClose} disabled={busy}>Cancel</button>
              <button className="acc-btn acc-btn-primary" onClick={submit} disabled={busy}>{busy ? 'Adding…' : 'Add target'}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Page shell ─────────────────────────────────────────────────────────
export default function NewsletterManagement() {
  const { tab: routeTab } = useParams();
  const history = useHistory();
  const userPermissions = useSelector((s) => s.user.userPermission);
  const companyPermissions = useSelector((s) => s.user.companyPermission);
  const allPermissions = useSelector((s) => s.user.allPermission);
  const isSuperAdmin = useSelector((s) => s.user.userInfo?.superAdmin === true);
  const FinalPermissions = useMemo(() => (
    (allPermissions || []).filter((p) => (companyPermissions || []).includes(p)).filter((p) => (userPermissions || []).includes(p))
  ), [allPermissions, companyPermissions, userPermissions]);
  // Super-admins see every tab/action regardless of package permissions —
  // matches ChatManagement.has() (without this, the Widget + Subscribers tabs,
  // both gated by newsletterChannelView, vanish for a super-admin whose
  // package doesn't explicitly include that permission).
  const has = (perm) => isSuperAdmin || FinalPermissions.includes(ALL_PERMISSIONS[perm]);

  const visibleTabs = TABS.filter((t) => has(t.perm));
  const tab = visibleTabs.find((t) => t.key === routeTab) || visibleTabs[0];

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // {null|'new'|<row>}
  const [viewing, setViewing] = useState(null); // issue row
  const [subsFor, setSubsFor] = useState(null); // channel row → subscribers modal
  const [embedFor, setEmbedFor] = useState(null); // channel row → embed modal
  // Header filter — brand + date (today / last week / last month / custom),
  // shared with the other system pages. Schedules carry nextDueAt, issues
  // scheduledFor — systemFilters picks whichever timestamp is present.
  const [filters, setFilters] = useState(EMPTY_SYSTEM_FILTERS);
  const brands = useBrandList();

  const refresh = useCallback(async () => {
    if (!tab) return;
    setLoading(true);
    try {
      const r = await get(tab.endpoint);
      setRows((r && r.data) || []);
    } catch (_) { setRows([]); }
    finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { refresh(); }, [refresh]);

  const setTab = (k) => history.push(`/newsletter-management/${k}`);

  // Server "Generate now" — emit a NEWSLETTER_GENERATE_REQUESTED for
  // the schedule. Same outbox event the daily scheduler emits, so the
  // worker pipeline is unified.
  const generateNow = async (schedule) => {
    if (!has('NEWSLETTER_ISSUE_GENERATE')) return;
    if (!window.confirm(`Generate a draft issue from "${schedule.name}" now?`)) return;
    try {
      await post(`/api/email/newsletters/schedules/${schedule._id}/generate`, {}, false);
      alert('Generation queued. Switch to the Issues tab to see progress.');
    } catch (e) {
      alert('Generate failed: ' + ((e && e.message) || 'unknown error'));
    }
  };

  // Soft-delete a subscription widget channel.
  const deleteChannel = async (channel) => {
    if (!has('NEWSLETTER_CHANNEL_DELETE')) return;
    if (!window.confirm(`Delete the "${channel.name}" widget? Any embedded snippet stops collecting subscribers.`)) return;
    try {
      await remove(`/api/email/newsletters/channels/${channel._id}`, {});
      refresh();
    } catch (e) {
      alert('Delete failed: ' + ((e && e.message) || 'unknown error'));
    }
  };

  if (!tab) {
    return (
      <div style={{ padding: 30 }}>
        <h3>Newsletter Management</h3>
        <div className="em-stub">You don't have access to any Newsletter tab. Ask your admin to grant a Newsletter permission.</div>
      </div>
    );
  }

  // Cost columns are super-admin-only — tenant admins never see
  // provider $ figures, only super-admins do.
  const cols = (COLUMNS[tab.key] || []).filter(
    ([key]) => isSuperAdmin || (key !== 'aiCostUSD' && key !== 'totalAiCostUSD')
  );

  // Brand + date filtering happens client-side over the fetched rows so the
  // header controls stay instant and consistent with the other systems.
  const visibleRows = applySystemFilter(rows, filters);

  // brand_id → name, so the Brand column shows the brand a target/widget
  // belongs to instead of a raw id. `brands` is already the caller's
  // accessible brands (useBrandList), which is also what scopes the picker.
  const brandsById = {};
  (brands || []).forEach((b) => { brandsById[String(b._id)] = b.name; });
  const brandName = (id) => (id ? (brandsById[String(id)] || '—') : '— company-wide —');

  return (
    <div style={{ padding: 20 }}>
      <div className="d-flex align-items-baseline justify-content-between mb-3">
        <h3 className="mb-0">Newsletter Management</h3>
        {tab.key === 'schedules' && has('NEWSLETTER_SCHEDULE_ADD') && (
          <button className="acc-btn acc-btn-primary" onClick={() => setEditing('new')}>+ New schedule</button>
        )}
        {tab.key === 'channels' && has('NEWSLETTER_CHANNEL_ADD') && (
          <button className="acc-btn acc-btn-primary" onClick={() => setEditing('new')}>+ New widget</button>
        )}
        {tab.key === 'subscribers' && has('NEWSLETTER_CHANNEL_ADD') && (
          <button className="acc-btn acc-btn-primary" onClick={() => setEditing('new')}>+ Add target</button>
        )}
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid #e5e5e5' }}>
        {visibleTabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: '8px 14px', background: 'none', border: 'none',
              borderBottom: tab.key === t.key ? '2px solid #15172b' : '2px solid transparent',
              fontWeight: tab.key === t.key ? 600 : 400, fontSize: 13,
            }}
          >{t.label}</button>
        ))}
      </div>

      <SystemFilterBar
        filters={filters}
        setFilters={setFilters}
        brands={brands}
        resultCount={visibleRows.length}
        totalCount={rows.length}
      />

      {loading ? (
        <div style={{ fontSize: 12, color: '#888' }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div className="em-stub" style={{ padding: 16, background: '#fafbfc', border: '1px dashed #ccc', borderRadius: 6, color: '#888', fontSize: 13 }}>
          {tab.key === 'schedules'
            ? 'No newsletter schedules yet. Create one to start automated cadences.'
            : tab.key === 'channels'
              ? 'No subscription widgets yet. Create one to collect names + emails on your site (BidLight / Prigmar / free-builder) — they flow straight into the list your schedule delivers to.'
              : tab.key === 'subscribers'
                ? 'No newsletter targets yet for your brands. They appear as people opt in through your widgets — or add one manually with "+ Add target".'
                : 'No issues yet. They appear when a schedule fires (or you click "Generate now" on a schedule).'}
        </div>
      ) : visibleRows.length === 0 ? (
        <div className="em-stub" style={{ padding: 16, background: '#fafbfc', border: '1px dashed #ccc', borderRadius: 6, color: '#888', fontSize: 13 }}>
          No {tab.label.toLowerCase()} match the current filters.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="table table-sm" style={{ fontSize: 13 }}>
            <thead>
              <tr>{cols.map(([k, l]) => (<th key={k}>{l}</th>))}<th /></tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => (
                <tr key={row._id}>
                  {cols.map(([k]) => (
                    <td key={k}>
                      {k === 'brand_id'
                        ? brandName(row.brand_id)
                        : fmt(k.includes('.') ? k.split('.').reduce((o, kk) => (o ? o[kk] : null), row) : row[k], k)}
                    </td>
                  ))}
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {tab.key === 'schedules' && (
                      <>
                        {has('NEWSLETTER_SCHEDULE_EDIT') && (
                          <button className="acc-btn" style={{ fontSize: 11, padding: '2px 8px', marginRight: 4 }} onClick={() => setEditing(row)}>Edit</button>
                        )}
                        {has('NEWSLETTER_ISSUE_GENERATE') && (
                          <button className="acc-btn" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => generateNow(row)}>Generate now</button>
                        )}
                      </>
                    )}
                    {tab.key === 'issues' && (
                      <button className="acc-btn" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => setViewing(row)}>View</button>
                    )}
                    {tab.key === 'channels' && (
                      <>
                        <button className="acc-btn" style={{ fontSize: 11, padding: '2px 8px', marginRight: 4 }} onClick={() => setEmbedFor(row)}>Embed</button>
                        <button className="acc-btn" style={{ fontSize: 11, padding: '2px 8px', marginRight: 4 }} onClick={() => setSubsFor(row)}>Subscribers</button>
                        {has('NEWSLETTER_CHANNEL_EDIT') && (
                          <button className="acc-btn" style={{ fontSize: 11, padding: '2px 8px', marginRight: 4 }} onClick={() => setEditing(row)}>Edit</button>
                        )}
                        {has('NEWSLETTER_CHANNEL_DELETE') && (
                          <button className="acc-btn" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => deleteChannel(row)}>Delete</button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && tab.key === 'schedules' && (
        <ScheduleEditor
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
      {editing && tab.key === 'channels' && (
        <ChannelEditor
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => refresh()}
        />
      )}
      {editing && tab.key === 'subscribers' && (
        <AddTargetModal
          brands={brands}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
      {viewing && (
        <IssueViewer
          issue={viewing}
          onClose={() => setViewing(null)}
          onChanged={() => { setViewing(null); refresh(); }}
        />
      )}
      {embedFor && <EmbedModal channel={embedFor} onClose={() => setEmbedFor(null)} />}
      {subsFor && <SubscribersModal channel={subsFor} onClose={() => setSubsFor(null)} />}
    </div>
  );
}
